import { Link, useParams } from "react-router-dom";
import Badge from "@/components/base/Badge";
import DocumentPdfButton from "@/components/base/DocumentPdfButton";
import PrintButton from "@/components/base/PrintButton";
import EmptyState from "@/components/base/EmptyState";
import { useAgenceData } from "@/pages/agence/data/AgenceDataProvider";
import {
  AGENCE_BIEN_TYPE_LABELS,
  AGENCE_CONTRAT_STATUT_LABELS,
  AGENCE_CONTRAT_STATUT_TONES,
  agenceContratReference,
  agenceLocataireNomComplet,
} from "@/pages/agence/data/types";
import { useProprietaires } from "@/pages/agence/proprietaires/ProprietairesProvider";
import { proprietaireNomComplet } from "@/pages/agence/proprietaires/types";
import { formatDate, formatFcfa } from "@/utils/format";
import { exportSummaryPdf, printSummaryPdf, type PdfDocumentOptions, type PdfSection } from "@/utils/pdfDocument";

export default function AgenceContratDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { getContrat, getBien, getImmeuble, getLocataire } = useAgenceData();
  const { getProprietaire } = useProprietaires();
  const contrat = id ? getContrat(id) : undefined;

  if (!contrat) {
    return (
      <div className="flex flex-col gap-6">
        <Link
          to="/agence/contrats"
          className="inline-flex w-fit items-center gap-2 whitespace-nowrap text-sm font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
        >
          <i className="ri-arrow-left-line text-base" aria-hidden="true" />
          Retour aux contrats
        </Link>
        <EmptyState
          icon="ri-file-text-line"
          title="Contrat introuvable"
          description="Ce contrat n'existe pas ou n'est plus disponible. Retournez à la liste pour le retrouver."
          action={
            <Link
              to="/agence/contrats"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-arrow-left-line text-base" aria-hidden="true" />
              Voir tous les contrats
            </Link>
          }
        />
      </div>
    );
  }

  const bien = getBien(contrat.bienId);
  const immeuble = getImmeuble(contrat.immeubleId);
  const locataire = getLocataire(contrat.locataireId);
  const proprietaire = getProprietaire(contrat.proprietaireId);
  const reference = agenceContratReference(contrat.id);

  const infos: { label: string; value: string; icon: string }[] = [
    { label: "Référence", value: reference, icon: "ri-price-tag-3-line" },
    {
      label: "Bien",
      value: bien ? `${AGENCE_BIEN_TYPE_LABELS[bien.type]}${bien.reference ? ` · ${bien.reference}` : ""}` : "—",
      icon: "ri-home-4-line",
    },
    { label: "Date de début", value: formatDate(contrat.dateDebut), icon: "ri-calendar-check-line" },
    { label: "Date de fin", value: contrat.dateFin ? formatDate(contrat.dateFin) : "—", icon: "ri-calendar-line" },
    { label: "Loyer mensuel", value: formatFcfa(contrat.loyerMensuel), icon: "ri-wallet-3-line" },
    { label: "Caution", value: contrat.caution > 0 ? formatFcfa(contrat.caution) : "—", icon: "ri-shield-check-line" },
    { label: "Avance", value: contrat.avance > 0 ? formatFcfa(contrat.avance) : "—", icon: "ri-money-cny-circle-line" },
    {
      label: "Jour d'échéance",
      value: contrat.jourEcheance > 0 ? `Le ${contrat.jourEcheance} du mois` : "—",
      icon: "ri-calendar-event-line",
    },
    { label: "Statut", value: AGENCE_CONTRAT_STATUT_LABELS[contrat.statut], icon: "ri-checkbox-circle-line" },
  ];

  const liens: { label: string; to: string; icon: string; value: string }[] = [
    {
      label: "Propriétaire",
      to: proprietaire ? `/agence/proprietaires/${proprietaire.id}` : "/agence/proprietaires",
      icon: "ri-user-star-line",
      value: proprietaire ? proprietaireNomComplet(proprietaire) : "Non renseigné",
    },
    {
      label: "Immeuble",
      to: immeuble ? `/agence/immeubles/${immeuble.id}` : "/agence/immeubles",
      icon: "ri-building-2-line",
      value: immeuble ? immeuble.nom : "Non renseigné",
    },
    {
      label: "Bien",
      to: bien ? `/agence/biens/${bien.id}` : "/agence/biens",
      icon: "ri-home-4-line",
      value: bien ? `${AGENCE_BIEN_TYPE_LABELS[bien.type]}${bien.reference ? ` · ${bien.reference}` : ""}` : "Non renseigné",
    },
    {
      label: "Locataire",
      to: locataire ? `/agence/locataires/${locataire.id}` : "/agence/locataires",
      icon: "ri-user-3-line",
      value: locataire ? agenceLocataireNomComplet(locataire) : "Non renseigné",
    },
  ];

  const buildPdfOptions = (): PdfDocumentOptions => {
    const sections: PdfSection[] = [
      {
        heading: "Informations du contrat",
        fields: infos.map((info) => ({ label: info.label, value: info.value })),
      },
      {
        heading: "Éléments liés",
        fields: liens.map((lien) => ({ label: lien.label, value: lien.value })),
      },
    ];
    if (contrat.document) {
      sections.push({ heading: "Document du contrat", note: contrat.document });
    }
    if (contrat.conditionsParticulieres) {
      sections.push({ heading: "Conditions particulières", note: contrat.conditionsParticulieres });
    }
    return {
      filename: `contrat-${reference}`,
      title: reference,
      subtitle: locataire ? agenceLocataireNomComplet(locataire) : "Contrat de location",
      sections,
    };
  };

  const handleExportPdf = () => exportSummaryPdf(buildPdfOptions());
  const handlePrint = () => printSummaryPdf(buildPdfOptions());

  return (
    <div className="flex flex-col gap-6">
      <Link
        to="/agence/contrats"
        className="inline-flex w-fit items-center gap-2 whitespace-nowrap text-sm font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
      >
        <i className="ri-arrow-left-line text-base" aria-hidden="true" />
        Retour aux contrats
      </Link>

      <section className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-6 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 items-start gap-4">
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
            <i className="ri-file-text-line text-2xl" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={AGENCE_CONTRAT_STATUT_TONES[contrat.statut]}>{AGENCE_CONTRAT_STATUT_LABELS[contrat.statut]}</Badge>
              {bien && (
                <Badge tone="secondary" icon="ri-home-4-line">
                  {AGENCE_BIEN_TYPE_LABELS[bien.type]}
                </Badge>
              )}
              {proprietaire && (
                <Badge tone="primary" icon="ri-user-star-line">
                  {proprietaireNomComplet(proprietaire)}
                </Badge>
              )}
            </div>
            <h2 className="mt-3 font-heading text-xl font-semibold text-foreground-950 md:text-2xl">{reference}</h2>
            <p className="mt-1.5 flex items-center gap-1.5 text-sm text-foreground-600">
              <i className="ri-user-3-line text-base text-foreground-400" aria-hidden="true" />
              <span className="truncate">{locataire ? agenceLocataireNomComplet(locataire) : "Locataire non renseigné"}</span>
            </p>
            <p className="mt-1 text-xs text-foreground-400">
              Du {formatDate(contrat.dateDebut)}
              {contrat.dateFin ? ` au ${formatDate(contrat.dateFin)}` : ""}
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row lg:shrink-0">
          {locataire && (
            <Link
              to={`/agence/locataires/${locataire.id}`}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-user-3-line text-base" aria-hidden="true" />
              Voir le locataire
            </Link>
          )}
          <Link
            to="/agence/contrats"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-4 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
          >
            <i className="ri-list-check" aria-hidden="true" />
            Tous les contrats
          </Link>
          <DocumentPdfButton onExport={handleExportPdf} />
          <PrintButton onPrint={handlePrint} />
        </div>
      </section>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <section className="flex flex-col rounded-lg border border-background-200 bg-background-50 lg:col-span-2">
          <header className="flex items-center gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary-50 text-secondary-600">
              <i className="ri-information-line text-base" aria-hidden="true" />
            </span>
            <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">
              Informations du contrat
            </h3>
          </header>
          <div className="p-4 md:p-5">
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {infos.map((info) => (
                <div key={info.label} className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-500">
                    <i className={`${info.icon} text-base`} aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <dt className="text-xs text-foreground-500">{info.label}</dt>
                    <dd className="truncate text-sm font-label font-medium text-foreground-950">{info.value}</dd>
                  </div>
                </div>
              ))}
            </dl>

            <div className="mt-5 border-t border-background-200 pt-4">
              <p className="text-xs font-label font-semibold uppercase tracking-wide text-foreground-400">
                Éléments liés
              </p>
              <div className="mt-2 flex flex-col gap-2">
                {liens.map((lien) => (
                  <Link
                    key={lien.label}
                    to={lien.to}
                    className="link-arrow flex items-center gap-3 rounded-md border border-foreground-200 bg-background-50 px-3.5 py-2.5 transition-colors hover:border-foreground-300 hover:bg-background-100 cursor-pointer"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-600">
                      <i className={`${lien.icon} text-base`} aria-hidden="true" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[11px] text-foreground-500">{lien.label}</span>
                      <span className="block truncate text-sm font-label font-medium text-foreground-950">
                        {lien.value}
                      </span>
                    </span>
                    <i className="ri-arrow-right-s-line shrink-0 text-base text-foreground-400" aria-hidden="true" />
                  </Link>
                ))}
              </div>
            </div>

            {contrat.document && (
              <div className="mt-5 border-t border-background-200 pt-4">
                <p className="text-xs font-label font-semibold uppercase tracking-wide text-foreground-400">
                  Document du contrat
                </p>
                <p className="mt-2 flex items-center gap-2 rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-700">
                  <i className="ri-file-paper-2-line text-base text-foreground-400" aria-hidden="true" />
                  <span className="truncate">{contrat.document}</span>
                </p>
              </div>
            )}

            {contrat.conditionsParticulieres && (
              <div className="mt-5 border-t border-background-200 pt-4">
                <p className="text-xs font-label font-semibold uppercase tracking-wide text-foreground-400">
                  Conditions particulières
                </p>
                <p className="mt-2 text-sm leading-relaxed text-foreground-700">{contrat.conditionsParticulieres}</p>
              </div>
            )}
          </div>
        </section>

        <section className="flex h-fit flex-col rounded-lg border border-background-200 bg-background-50">
          <header className="flex items-center gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-50 text-accent-700">
              <i className="ri-links-line text-base" aria-hidden="true" />
            </span>
            <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">Accès rapides</h3>
          </header>
          <div className="flex flex-col gap-1 p-2">
            {[
              { label: "Locataire", to: locataire ? `/agence/locataires/${locataire.id}` : "/agence/locataires", icon: "ri-user-3-line", hint: "Fiche de l'occupant" },
              { label: "Bien", to: bien ? `/agence/biens/${bien.id}` : "/agence/biens", icon: "ri-home-4-line", hint: "Logement concerné" },
              { label: "Immeuble", to: immeuble ? `/agence/immeubles/${immeuble.id}` : "/agence/immeubles", icon: "ri-building-2-line", hint: "Immeuble du bien" },
              { label: "Propriétaire", to: proprietaire ? `/agence/proprietaires/${proprietaire.id}` : "/agence/proprietaires", icon: "ri-user-star-line", hint: "Propriétaire du bien" },
              { label: "Loyers", to: `/agence/loyers?contrat=${contrat.id}`, icon: "ri-wallet-3-line", hint: "Échéances du contrat" },
              { label: "Paiements", to: `/agence/paiements?contrat=${contrat.id}`, icon: "ri-hand-coin-line", hint: "Encaissements reçus" },
              { label: "Documents", to: `/agence/documents?contrat=${contrat.id}`, icon: "ri-folder-line", hint: "Contrat et justificatifs" },
            ].map((module) => (
              <Link
                key={module.to}
                to={module.to}
                className="flex items-center gap-3 rounded-md px-3 py-2.5 transition-colors hover:bg-background-100 cursor-pointer"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-600">
                  <i className={`${module.icon} text-base`} aria-hidden="true" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-label font-medium text-foreground-950">
                    {module.label}
                  </span>
                  <span className="block truncate text-xs text-foreground-500">{module.hint}</span>
                </span>
                <i className="ri-arrow-right-s-line shrink-0 text-base text-foreground-400" aria-hidden="true" />
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}